import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParsedInvoiceProduct } from 'shared/types/Invoice';
import { ProductInvoiceModel } from 'shared/types/Products';

import SelectItemsModal from '@/components/SelectItemsModal';
import { useDatabase } from '@/hooks';

import InvoicingProductsList from './InvoicingProductsList';

type Props = {
    close: VoidFunction;
    isOpen: boolean;
    onSelectProducts: (products: ParsedInvoiceProduct[]) => void;
    selectedProducts: ParsedInvoiceProduct[];
};

function SelectInvoicingProductsModal({
    close,
    isOpen,
    onSelectProducts,
    selectedProducts,
}: Props) {
    const { t } = useTranslation();
    const { getDatabase } = useDatabase();

    const database = useMemo(() => getDatabase(), [getDatabase]);

    const fetchProducts = useCallback(
        async (query = '') => {
            const collection =
                database.collections.get<ProductInvoiceModel>(
                    'invoicing_products',
                );

            let products = await collection.query().fetch();

            if (query) {
                const lowerQuery = query.toLowerCase();
                products = products.filter((p) =>
                    p.name.toLowerCase().includes(lowerQuery),
                );
            }

            return products;
        },
        [database],
    );

    const handleSelectProducts = useCallback(
        (items: ProductInvoiceModel[] | ProductInvoiceModel) => {
            const productModels = Array.isArray(items) ? items : [items];

            const parsed: ParsedInvoiceProduct[] = productModels.map(
                (product) => {
                    const existing = selectedProducts.find(
                        (sp) => sp.invoicingProductId === product.id,
                    );

                    return {
                        id: existing?.id,
                        invoicingProductId: product.id,
                        name: product.name,
                        price: product.price,
                        userId: product.userId,
                        organisationId: product.organisationId,
                        quantity: existing?.quantity || 1,
                    };
                },
            );

            onSelectProducts(parsed);
        },
        [onSelectProducts, selectedProducts],
    );

    const [selectedModels, setSelectedModels] = useState<ProductInvoiceModel[]>(
        [],
    );

    useEffect(() => {
        if (!isOpen) return;

        async function fetchSelectedModels() {
            const collection =
                database.collections.get<ProductInvoiceModel>(
                    'invoicing_products',
                );
            const models = await Promise.all(
                selectedProducts
                    .filter((sp) => sp.invoicingProductId)
                    .map((sp) => collection.find(sp.invoicingProductId!)),
            );
            setSelectedModels(models);
        }

        fetchSelectedModels();
    }, [isOpen, selectedProducts, database]);

    return (
        <SelectItemsModal<
            ProductInvoiceModel,
            React.ComponentProps<typeof InvoicingProductsList>
        >
            fetchItemsFn={fetchProducts}
            selectedItems={selectedModels}
            isOpen={isOpen}
            onSelectItems={handleSelectProducts}
            close={close}
            emptyMessage={t('Invoice:labels:no_products_added')}
            noItemsFoundMessage={t('Invoice:labels:no_products_added')}
            selectedItemsCountTextKey="Invoice:labels:products_selected"
            addSelectedItemsButtonText={t(
                'Invoice:buttons:add_selected_products',
            )}
            testIdPrefix="SelectInvoicingProductsModal"
            title={t('Invoice:buttons:select_products')}
            ListComponent={InvoicingProductsList}
            listComponentProps={{
                testIdPrefix: 'SelectInvoicingProductsModal',
            }}
            multiSelect
        />
    );
}

export default SelectInvoicingProductsModal;
